import { inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ApiResponse, ApiService } from '../api/api.service';

export interface WeatherTemperature {
    degrees: number;
    unit: 'CELSIUS' | 'FAHRENHEIT' | string;
}

export interface WeatherCondition {
    iconBaseUri: string;
    description: {
        text: string;
        languageCode: string;
    };
    type: string;
}

export interface CurrentWeather {
    currentTime: string;
    timeZone: { id: string };
    isDaytime: boolean;
    weatherCondition: WeatherCondition;
    temperature: WeatherTemperature;
    feelsLikeTemperature: WeatherTemperature;
    dewPoint: WeatherTemperature;
    heatIndex: WeatherTemperature;
    windChill: WeatherTemperature;
    relativeHumidity: number;
    wind: {
        direction: { degrees: number; cardinal: string };
        speed: { value: number; unit: string };
        gust: { value: number; unit: string };
    };
    visibility: { distance: number; unit: string };
    cloudCover: number;
    uvIndex: number;
    precipitation: { probability: { percent: number; type: string }; qpf: { quantity: number; unit: string } };
    thunderstormProbability: number;
    airPressure: { meanSeaLevelMillibars: number };
}

@Injectable({
    providedIn: 'root',
})
export class WeatherService {
    private apiService = inject(ApiService);

    readonly currentWeather = signal<CurrentWeather | null>(null);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    private cachedCoords: GeolocationCoordinates | null = null;

    prefetchLocation(): void {
        this.getCurrentPosition().subscribe({
            next: coords => (this.cachedCoords = coords),
            error: () => {},
        });
    }

    getCurrentWeather(): Observable<ApiResponse<CurrentWeather>> {
        this.loading.set(true);
        this.error.set(null);

        const coords$ = this.cachedCoords
            ? new Observable<GeolocationCoordinates>(o => {
                  o.next(this.cachedCoords!);
                  o.complete();
              })
            : this.getCurrentPosition();

        return coords$.pipe(
            tap(coords => (this.cachedCoords = coords)),
            switchMap((coords: GeolocationCoordinates) => this.getWeather(coords.latitude, coords.longitude)),
            tap({
                next: response => {
                    this.currentWeather.set(response.data);
                    this.loading.set(false);
                },
                error: (err: Error) => {
                    this.error.set(err?.message ?? 'Kon het weer niet ophalen.');
                    this.loading.set(false);
                },
            })
        );
    }

    private getCurrentPosition(): Observable<GeolocationCoordinates> {
        return new Observable(observer => {
            if (!navigator.geolocation) {
                observer.error(new Error('Geolocatie wordt niet ondersteund door deze browser.'));
                return;
            }
            navigator.geolocation.getCurrentPosition(
                position => {
                    observer.next(position.coords);
                    observer.complete();
                },
                err => observer.error(new Error(`Locatie kon niet worden bepaald: ${err.message}`))
            );
        });
    }

    private getWeather(latitude: number, longitude: number): Observable<ApiResponse<CurrentWeather>> {
        return this.apiService.getCall$<CurrentWeather>('weather/current', { latitude, longitude });
    }
}
